import * as THREE from "three";
import type { Product3DAssetBounds } from "../../products/product3DTypes";

const MIN_MODEL_SIZE = 0.01;
const ROTATION_EPSILON = 1e-8;

export type Symbol3DAssetFitMode =
  | "contain3d"
  | "footprint"
  | "stretchFootprint";

type Rotation3D = { pitch?: number; roll?: number; yaw?: number } | undefined;

/**
 * Computes effective floor distance (lowest rendered Z above floor) for a 3D asset.
 *
 * This uses the same sizing strategy as {@link computeFloorplannerAssetTransform}
 * and rotates the source bounds corners so "Distance from floor = 0" always
 * corresponds to the model touching floor level.
 */
export function computeAssetEffectiveFloorDistance(
  baseElevation: number,
  rotation3D: Rotation3D,
  bounds: Product3DAssetBounds | undefined,
  targetWidth: number,
  targetDepth: number,
  targetHeight: number,
  fitMode: Symbol3DAssetFitMode,
): number {
  const normalized = getNormalizedBounds(bounds);
  if (!normalized) return baseElevation;

  const { minZScaleDelta } = getMinScaledRotatedZDelta(
    normalized,
    rotation3D,
    targetWidth,
    targetDepth,
    targetHeight,
    fitMode,
  );
  const effective = baseElevation + minZScaleDelta;
  return Math.abs(effective) < ROTATION_EPSILON ? 0 : effective;
}

/**
 * Inverse of {@link computeAssetEffectiveFloorDistance}; converts a desired
 * effective floor distance to the raw base elevation persisted in symbol.threeD.
 */
export function assetBaseElevationFromEffectiveFloor(
  targetEffectiveFloor: number,
  rotation3D: Rotation3D,
  bounds: Product3DAssetBounds | undefined,
  targetWidth: number,
  targetDepth: number,
  targetHeight: number,
  fitMode: Symbol3DAssetFitMode,
): number {
  const normalized = getNormalizedBounds(bounds);
  if (!normalized) return targetEffectiveFloor;

  const { minZScaleDelta } = getMinScaledRotatedZDelta(
    normalized,
    rotation3D,
    targetWidth,
    targetDepth,
    targetHeight,
    fitMode,
  );
  const baseElevation = targetEffectiveFloor - minZScaleDelta;
  return Math.abs(baseElevation) < ROTATION_EPSILON ? 0 : baseElevation;
}

function getNormalizedBounds(
  bounds: Product3DAssetBounds | undefined,
): { min: THREE.Vector3; max: THREE.Vector3 } | null {
  const min = bounds?.min;
  const max = bounds?.max;
  if (!min || !max || min.length < 3 || max.length < 3) return null;
  const minVec = new THREE.Vector3(min[0], min[1], min[2]);
  const maxVec = new THREE.Vector3(max[0], max[1], max[2]);
  return { min: minVec.min(maxVec.clone()), max: maxVec.max(minVec.clone()) };
}

function getMinScaledRotatedZDelta(
  bounds: { min: THREE.Vector3; max: THREE.Vector3 },
  rotation3D: Rotation3D,
  targetWidth: number,
  targetDepth: number,
  targetHeight: number,
  fitMode: Symbol3DAssetFitMode,
): { minZScaleDelta: number } {
  const size = bounds.max.clone().sub(bounds.min);
  const scale = getScaleComponents(
    targetWidth,
    targetDepth,
    targetHeight,
    size,
    fitMode,
  );
  const corners = createBoundsCorners(bounds.min, bounds.max);
  const minRotatedZ = getMinRotatedZ(corners, rotation3D);
  return { minZScaleDelta: (minRotatedZ - bounds.min.z) * scale[2] };
}

function getScaleComponents(
  targetWidth: number,
  targetDepth: number,
  targetHeight: number,
  size: THREE.Vector3,
  fitMode: Symbol3DAssetFitMode,
): [number, number, number] {
  const footprintScaleX = safeScale(targetWidth, size.x);
  const footprintScaleY = safeScale(targetDepth, size.y);
  const heightScale = safeScale(targetHeight, size.z);

  if (fitMode === "stretchFootprint") {
    return [footprintScaleX, footprintScaleY, heightScale];
  }
  if (fitMode === "footprint") {
    const footprintScale = Math.min(footprintScaleX, footprintScaleY);
    return [footprintScale, footprintScale, heightScale];
  }
  const uniformScale = Math.min(footprintScaleX, footprintScaleY, heightScale);
  return [uniformScale, uniformScale, uniformScale];
}

function safeScale(target: number, source: number): number {
  const safeTarget =
    Number.isFinite(target) && target > 0 ? target : MIN_MODEL_SIZE;
  const safeSource =
    Number.isFinite(source) && source > 0 ? source : MIN_MODEL_SIZE;
  return safeTarget / safeSource;
}

function createBoundsCorners(
  min: THREE.Vector3,
  max: THREE.Vector3,
): THREE.Vector3[] {
  return [
    new THREE.Vector3(min.x, min.y, min.z),
    new THREE.Vector3(min.x, min.y, max.z),
    new THREE.Vector3(min.x, max.y, min.z),
    new THREE.Vector3(min.x, max.y, max.z),
    new THREE.Vector3(max.x, min.y, min.z),
    new THREE.Vector3(max.x, min.y, max.z),
    new THREE.Vector3(max.x, max.y, min.z),
    new THREE.Vector3(max.x, max.y, max.z),
  ];
}

function getMinRotatedZ(corners: THREE.Vector3[], rotation3D: Rotation3D): number {
  const pitch = rotation3D?.pitch ?? 0;
  const roll = rotation3D?.roll ?? 0;
  const yaw = rotation3D?.yaw ?? 0;
  if (
    Math.abs(pitch) < ROTATION_EPSILON &&
    Math.abs(roll) < ROTATION_EPSILON &&
    Math.abs(yaw) < ROTATION_EPSILON
  ) {
    return Math.min(...corners.map((corner) => corner.z));
  }

  const matrix = new THREE.Matrix4().makeRotationFromEuler(
    new THREE.Euler(pitch, roll, yaw, "XYZ"),
  );
  let minZ = Infinity;
  const tmp = new THREE.Vector3();
  corners.forEach((corner) => {
    tmp.copy(corner).applyMatrix4(matrix);
    if (tmp.z < minZ) minZ = tmp.z;
  });
  return minZ;
}
